import {
  ApiRef,
  ConfigApi,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { LaunchDarklyEvaluationReasonKinds } from './types';

/** Content-Type for [semantic patch](https://launchdarkly.com/docs/api/feature-flags/patch-feature-flag) on feature flags. */
export const LAUNCHDARKLY_SEMANTIC_PATCH_CONTENT_TYPE =
  'application/json; domain-model=launchdarkly.semanticpatch';

/** Semantic patch kinds for individual context targets  */
export const LAUNCHDARKLY_PATCH_ADD_TARGETS = 'addTargets' as const;
export const LAUNCHDARKLY_PATCH_REMOVE_TARGETS = 'removeTargets' as const;

/**
 * One semantic-patch instruction targeting context keys for a variation (e.g. addTargets / removeTargets).
 * See [Patch feature flag](https://launchdarkly.com/docs/api/feature-flags/patch-feature-flag).
 */
export interface LaunchDarklyFlagSemanticPatchInstruction {
  kind: string;
  contextKind: string;
  values: string[];
  variationId: string;
}

/**
 * A flag variation from [Get feature flag](https://launchdarkly.com/docs/api/feature-flags/get-feature-flag).
 * `_id` is the variation id used by semantic patch instructions.
 */
export interface LaunchDarklyFlagVariation {
  _id?: string;
  value: unknown;
  name?: string;
  description?: string;
}

/** Variations (and ids/values) for a flag, scoped to the configured environment via `env` query. */
export interface FeatureFlagVariations {
  featureFlagKey: string;
  variations: LaunchDarklyFlagVariation[];
}

/**
 * Resolve LaunchDarkly variation `_id`s for strict boolean `true` / `false` values (last match wins).
 */
export function pickBooleanVariationIds(
  variations: LaunchDarklyFlagVariation[],
): { trueVariationId: string; falseVariationId: string } {
  let trueVariationId: string | undefined;
  let falseVariationId: string | undefined;

  for (const v of variations) {
    if (v.value === true && v._id) {
      trueVariationId = v._id;
    }
    if (v.value === false && v._id) {
      falseVariationId = v._id;
    }
  }

  if (!trueVariationId || !falseVariationId) {
    throw new Error(
      'Feature flag does not define boolean true/false variations with ids',
    );
  }

  return { trueVariationId, falseVariationId };
}

/** Arguments for {@link LaunchDarklyRestApi.setBooleanFeatureFlagForContext} (sre-portal `SetLaunchDarklyFeatureFlag`). */
export interface SetBooleanFeatureFlagParams {
  featureFlagKey: string;
  contextKind: string;
  contextKey: string;
  desiredState: boolean;
  comment?: string;
}

/** Parameters to PATCH a flag with a single context-targeting instruction (same shape as sre-portal `PatchFlag`). */
export interface PatchFeatureFlagParams {
  /** Feature flag key in the LaunchDarkly project (URL path segment). */
  featureFlagKey: string;
  /** Semantic instruction `kind` (e.g. addTargets, removeTargets). */
  patchKind: string;
  contextKind: string;
  contextKey: string;
  /** Variation `_id` from the flag definition. */
  variationId: string;
  comment?: string;
}

/** LaunchDarkly [evaluate context instance](https://launchdarkly.com/docs/api/contexts/evaluate-context-instance) response item. */
export interface LaunchDarklyContextEvaluation {
  name: string;
  key: string;
  _value: unknown;
  tags?: string[];
  reason?: LaunchDarklyEvaluationReason;
  _links: Record<string, { href: string; type?: string }>;
}

export interface LaunchDarklyEvaluationReason {
  kind: LaunchDarklyEvaluationReasonKinds;
  ruleIndex?: number;
  ruleID?: string;
  prerequisiteKey?: string;
  inExperiment?: boolean;
  errorKind?: string;
}

export interface LaunchDarklyContextEvaluations {
  items: LaunchDarklyContextEvaluation[];
  totalCount?: number;
  _links: Record<string, { href: string; type?: string }>;
}

/**
 * Context body for POST …/flags/evaluate (LaunchDarkly ContextInstance).
 * `kind` and `key` are required; other attributes are allowed.
 */
export type LaunchDarklyContextInstance = {
  kind: string;
  key: string;
} & Record<string, unknown>;

export interface EvaluateFlagsForContextParams {
  context: LaunchDarklyContextInstance;
  /**
   * Flag keys or names to keep in the result. Matching is case-insensitive against
   * each item’s `key` and `name`. Applied only after the evaluate response (not in the LD `filter` param).
   */
  flagNames?: string[];
  /** Tags; sent as LaunchDarkly `tags contains` on the request (server-side). */
  tags?: string[];
}

/** Allowed characters for flag keys / tags used in the `filter` query param (avoid filter injection). */
const SAFE_LD_FILTER_SEGMENT = /^[a-zA-Z0-9_.-]+$/;

function assertSafeFilterSegment(value: string, label: string): void {
  if (!SAFE_LD_FILTER_SEGMENT.test(value)) {
    throw new Error(
      `Invalid ${label}: use only letters, digits, underscore, hyphen, and period`,
    );
  }
}

function assertSafePatchParam(value: string, label: string): void {
  if (!value.trim()) {
    throw new Error(`${label} is required`);
  }
  if (!SAFE_LD_FILTER_SEGMENT.test(value)) {
    throw new Error(
      `Invalid ${label}: use only letters, digits, underscore, hyphen, and period`,
    );
  }
}

function normalizeDistinctStrings(values?: string[]): string[] {
  const trimmed = (values ?? []).map(v => v.trim());
  const nonEmpty = trimmed.filter(Boolean);
  return [...new Set(nonEmpty)];
}

/**
 * Builds the LaunchDarkly `filter` query string for [evaluate context instance](https://launchdarkly.com/docs/api/contexts/evaluate-context-instance).
 * Only `tags` are sent server-side; narrow by flag key/name in application code after the response.
 */
export function buildLaunchDarklyEvaluateFilter(params: {
  tags?: string[];
}): string | undefined {
  const tags = normalizeDistinctStrings(params.tags);
  tags.forEach(t => assertSafeFilterSegment(t, 'tag'));
  if (!tags.length) {
    return undefined;
  }
  return `tags contains ${JSON.stringify(tags)}`;
}

function evaluationMatchesFlagName(
  item: LaunchDarklyContextEvaluation,
  wanted: string,
): boolean {
  const w = wanted.toLowerCase();
  return item.key.toLowerCase() === w || item.name.toLowerCase() === w;
}

function filterEvaluationsByFlagNames(
  items: LaunchDarklyContextEvaluation[],
  flagNames: string[],
): LaunchDarklyContextEvaluation[] {
  if (!flagNames.length) {
    return items;
  }
  return items.filter(item =>
    flagNames.some(w => evaluationMatchesFlagName(item, w)),
  );
}

export class LaunchDarklyRestApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly projectKey: string;
  private readonly environmentKey: string;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    config: ConfigApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.projectKey = options.config.getString(`neo4jLaunchDarkly.projectKey`);
    this.environmentKey = options.config.getString(
      `neo4jLaunchDarkly.environmentKey`,
    );
  }

  private evaluatePathAndQuery(filter?: string): string {
    const path = `v2/projects/${encodeURIComponent(this.projectKey)}/environments/${encodeURIComponent(this.environmentKey)}/flags/evaluate`;
    if (!filter) {
      return path;
    }
    return `${path}?${new URLSearchParams({ filter }).toString()}`;
  }

  private async postEvaluate(
    context: LaunchDarklyContextInstance,
    filter?: string,
  ): Promise<LaunchDarklyContextEvaluations> {
    const apiPath = this.evaluatePathAndQuery(filter);
    const proxyBase = await this.discoveryApi.getBaseUrl('proxy');
    const url = `${proxyBase.replace(/\/$/, '')}/neo4j-launch-darkly/${apiPath}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(context),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response.json();
  }

  /**
   * Evaluates flags for a context via the proxied LaunchDarkly REST API.
   * Optional `tags` become the LaunchDarkly `filter` query only. Optional `flagNames` narrow
   * the returned `items` after the response (not sent as `query equals` to LaunchDarkly).
   */
  async evaluateFlagsForContext(
    params: EvaluateFlagsForContextParams,
  ): Promise<LaunchDarklyContextEvaluations> {
    const flagNames = normalizeDistinctStrings(params.flagNames);
    const tags = normalizeDistinctStrings(params.tags);

    const filter = buildLaunchDarklyEvaluateFilter({
      tags: tags.length ? tags : undefined,
    });
    const raw = await this.postEvaluate(params.context, filter);
    const items = filterEvaluationsByFlagNames(raw.items, flagNames);
    return {
      ...raw,
      items,
      totalCount: items.length,
    };
  }

  /**
   * PATCH flag with a [semantic patch](https://launchdarkly.com/docs/api/feature-flags/patch-feature-flag)
   * (single instruction: kind, context kind, context keys, variation id), proxied like evaluate calls.
   */
  async patchFeatureFlag(params: PatchFeatureFlagParams): Promise<void> {
    assertSafePatchParam(params.featureFlagKey, 'featureFlagKey');
    assertSafePatchParam(params.patchKind, 'patchKind');
    assertSafePatchParam(params.contextKind, 'contextKind');
    assertSafePatchParam(params.contextKey, 'contextKey');
    assertSafePatchParam(params.variationId, 'variationId');

    const path = `v2/flags/${encodeURIComponent(this.projectKey)}/${encodeURIComponent(params.featureFlagKey)}`;
    const proxyBase = await this.discoveryApi.getBaseUrl('proxy');
    const url = `${proxyBase.replace(/\/$/, '')}/neo4j-launch-darkly/${path}`;

    const body = {
      environmentKey: this.environmentKey,
      instructions: [
        {
          kind: params.patchKind,
          contextKind: params.contextKind,
          values: [params.contextKey],
          variationId: params.variationId,
        } satisfies LaunchDarklyFlagSemanticPatchInstruction,
      ],
      ...(params.comment !== undefined && params.comment !== ''
        ? { comment: params.comment }
        : {}),
    };

    const response = await this.fetchApi.fetch(url, {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': LAUNCHDARKLY_SEMANTIC_PATCH_CONTENT_TYPE,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  /**
   * Fetches a flag via [Get feature flag](https://launchdarkly.com/docs/api/feature-flags/get-feature-flag)
   * and returns its variations (`_id`, `value`, optional `name` / `description`).
   * Uses `env` set to the configured environment key to limit payload size.
   */
  async getFeatureFlagVariations(
    featureFlagKey: string,
  ): Promise<FeatureFlagVariations> {
    assertSafePatchParam(featureFlagKey, 'featureFlagKey');

    const path = `v2/flags/${encodeURIComponent(this.projectKey)}/${encodeURIComponent(featureFlagKey)}`;
    const search = new URLSearchParams({ env: this.environmentKey });
    const proxyBase = await this.discoveryApi.getBaseUrl('proxy');
    const url = `${proxyBase.replace(/\/$/, '')}/neo4j-launch-darkly/${path}?${search.toString()}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null) {
      throw new Error('Invalid feature flag response');
    }
    const record = body as Record<string, unknown>;
    const key = typeof record.key === 'string' ? record.key : featureFlagKey;
    const rawVariations = record.variations;
    if (!Array.isArray(rawVariations)) {
      return { featureFlagKey: key, variations: [] };
    }

    const variations: LaunchDarklyFlagVariation[] = rawVariations
      .filter(
        (item): item is Record<string, unknown> =>
          typeof item === 'object' && item !== null && 'value' in item,
      )
      .map(item => ({
        _id: typeof item._id === 'string' ? item._id : undefined,
        value: item.value,
        name: typeof item.name === 'string' ? item.name : undefined,
        description:
          typeof item.description === 'string' ? item.description : undefined,
      }));

    return { featureFlagKey: key, variations };
  }

  /**
   * For a boolean flag, set targeting so `contextKey` (of `contextKind`) receives `desiredState`:
   * [removeTargets](https://launchdarkly.com/docs/api/feature-flags/patch-feature-flag) on the opposite variation, then
   * [addTargets](https://launchdarkly.com/docs/api/feature-flags/patch-feature-flag) on the matching one
   */
  async setBooleanFeatureFlagForContext(
    params: SetBooleanFeatureFlagParams,
  ): Promise<void> {
    assertSafePatchParam(params.featureFlagKey, 'featureFlagKey');
    assertSafePatchParam(params.contextKind, 'contextKind');
    assertSafePatchParam(params.contextKey, 'contextKey');

    const { variations } = await this.getFeatureFlagVariations(
      params.featureFlagKey,
    );
    const { trueVariationId, falseVariationId } =
      pickBooleanVariationIds(variations);

    const desiredVariationId = params.desiredState
      ? trueVariationId
      : falseVariationId;
    const undesiredVariationId = params.desiredState
      ? falseVariationId
      : trueVariationId;

    await this.patchFeatureFlag({
      featureFlagKey: params.featureFlagKey,
      patchKind: LAUNCHDARKLY_PATCH_REMOVE_TARGETS,
      contextKind: params.contextKind,
      contextKey: params.contextKey,
      variationId: undesiredVariationId,
      comment: params.comment,
    });
    await this.patchFeatureFlag({
      featureFlagKey: params.featureFlagKey,
      patchKind: LAUNCHDARKLY_PATCH_ADD_TARGETS,
      contextKind: params.contextKind,
      contextKey: params.contextKey,
      variationId: desiredVariationId,
      comment: params.comment,
    });
  }
}

export const launchDarklyApiRef: ApiRef<LaunchDarklyRestApi> =
  createApiRef<LaunchDarklyRestApi>({
    id: 'plugin.neo4j-launch-darkly.launch-darkly.service',
  });
